// src/pages/LogPage.tsx — every operator action in every section, newest first (OPS-FINAL D6).
import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ops, POLICY_LABEL, type ActivityRow, type LogSection } from "../lib/ops";
import { dateTime, FIELD_LABEL, logValue } from "../lib/format";
import { useServices } from "../lib/context";
import { Chip, Loading, Notice, PageFrame } from "../components/ui";

const SECTIONS: { id: LogSection | "all"; label: string }[] = [
  { id: "all", label: "Everything" },
  { id: "dispatch", label: "Dispatch" },
  { id: "leads", label: "Leads" },
  { id: "territories", label: "Territories" },
  { id: "pricing", label: "Pricing" },
  { id: "campaigns", label: "Campaigns" },
];

// Keys that are bookkeeping, not decisions — never shown as a change.
const SKIP = new Set(["created_at", "updated_at", "decision_date", "total_revenue_cents", "total_cost_cents", "jobs_per_week", "scan_rate_pct", "entered_annual_rhythm_at", "test_drop_date", "zip"]);

function targetLink(r: ActivityRow, svcName: (id: string) => string | undefined) {
  const [kind, ...rest] = r.target.split(":");
  const id = rest.join(":");
  if (kind === "job" && id) return <Link to={`/dispatch?job=${encodeURIComponent(id)}`}>Job {id.slice(0, 8)}</Link>;
  if (kind === "zip" && id) return <Link to={`/territories?zip=${encodeURIComponent(id)}`}>ZIP {id}</Link>;
  if (kind === "lead" && id) return <>Lead {id.slice(0, 8)}</>;
  if (kind === "campaign" && id === "settings") return <>Discount approvals</>;
  if (kind === "campaign" && id) return <Link to={`/campaigns?campaign=${encodeURIComponent(id)}`}>Campaign {id}</Link>;
  const name = svcName(r.target);
  if (name) return <Link to={`/pricing/services?open=${encodeURIComponent(r.target)}`}>{name}</Link>;
  return <>{r.target}</>;
}

export default function LogPage() {
  const services = useServices();
  const [section, setSection] = useState<LogSection | "all">("all");
  const [filter, setFilter] = useState("");
  const [applied, setApplied] = useState("");
  const [rows, setRows] = useState<ActivityRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (sec: LogSection | "all", target: string) => {
    setRows(null);
    try {
      setRows(await ops.activityLog(200, sec === "all" ? undefined : sec, target || undefined));
      setApplied(target);
      setError(null);
    } catch (e) {
      setError((e as Error).message);
    }
  }, []);
  useEffect(() => {
    void load(section, applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load, section]);

  const svcName = (id: string) => services.data?.services.find((s) => s.id === id)?.name;

  return (
    <PageFrame title="Change log" lede="Every change made through this console, with who made it and why.">
      <div className="segmented wrap" role="group" aria-label="Section">
        {SECTIONS.map((s) => (
          <button key={s.id} type="button" className={section === s.id ? "seg on" : "seg"} aria-pressed={section === s.id} onClick={() => setSection(s.id)}>
            {s.label}
          </button>
        ))}
      </div>
      <form
        className="zip-search"
        onSubmit={(e) => {
          e.preventDefault();
          void load(section, filter.trim());
        }}
      >
        <input
          type="search"
          className="search"
          placeholder="Filter by what changed: a service id (sea-004), a ZIP, a job or lead id"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          aria-label="Filter by target"
        />
        <button type="submit" className="btn">
          Filter
        </button>
        {applied && (
          <button
            type="button"
            className="btn quiet"
            onClick={() => {
              setFilter("");
              void load(section, "");
            }}
          >
            Clear filter
          </button>
        )}
      </form>

      {!rows && !error && <Loading what="the change log" />}
      {error && <Notice kind="error">{error}</Notice>}
      {rows && rows.length === 0 && <p className="empty-state">No changes{applied ? ` for “${applied}”` : ""} yet.</p>}
      {rows && rows.length > 0 && (
        <ol className="log-list">
          {rows.map((r) => {
            const keys = Array.from(new Set([...Object.keys(r.old_value ?? {}), ...Object.keys(r.new_value ?? {})])).filter(
              (k) => !SKIP.has(k) && JSON.stringify(r.old_value?.[k] ?? null) !== JSON.stringify(r.new_value?.[k] ?? null),
            );
            return (
              <li key={r.id} className="log-item">
                <div className="log-head">
                  <span className="log-what">
                    {POLICY_LABEL[r.policy] ?? r.policy.replace(/_/g, " ")}: {targetLink(r, svcName)}
                  </span>
                  <span className="chips">
                    <Chip tone="muted">{SECTIONS.find((s) => s.id === r.section)?.label ?? r.section}</Chip>
                    {r.ack_below_cost && <Chip tone="warn">Below cost acknowledged</Chip>}
                  </span>
                </div>
                <p className="log-meta">
                  {dateTime(r.changed_at)} by {r.changed_by_email ?? "unknown"}
                </p>
                {keys.length > 0 && (
                  <ul className="log-diff">
                    {keys.map((k) => (
                      <li key={k}>
                        <span className="field-name">{FIELD_LABEL[k] ?? k.replace(/_/g, " ")}</span>{" "}
                        <span className="money">{logValue(k, r.old_value?.[k])}</span> to{" "}
                        <span className="money">{logValue(k, r.new_value?.[k])}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="log-reason">“{r.reason}”</p>
              </li>
            );
          })}
        </ol>
      )}
    </PageFrame>
  );
}
